import { useCallback, useEffect, useRef, useState } from "react";
import api from "./api";

/**
 * Run a long AI job by dispatching it to the backend, then polling until done.
 *
 * Backend contract:
 *   POST {dispatchPath} → { job_id, status: "pending" }
 *   GET /ai/jobs/{job_id} → { status, result, error }
 *
 * Returns: { run, loading, elapsedSeconds, cancel }
 */
export function useAiJob() {
  const [loading, setLoading] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const cancelRef = useRef(false);
  const startedAtRef = useRef(0);
  const tickRef = useRef(null);

  useEffect(() => {
    return () => {
      cancelRef.current = true;
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, []);

  const run = useCallback(async (dispatchPath, body, { maxSeconds = 300 } = {}) => {
    cancelRef.current = false;
    setLoading(true);
    setElapsedSeconds(0);
    startedAtRef.current = Date.now();
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      setElapsedSeconds(Math.round((Date.now() - startedAtRef.current) / 1000));
    }, 1000);

    try {
      const { data: dispatch } = await api.post(dispatchPath, body, { timeout: 20000 });
      const jobId = dispatch.job_id;
      if (!jobId) throw new Error("Job ID mancante dalla risposta");

      // Poll every 2s until done/error
      const deadline = Date.now() + maxSeconds * 1000;
      while (Date.now() < deadline) {
        if (cancelRef.current) throw new Error("Annullato");
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 2000));
        if (cancelRef.current) throw new Error("Annullato");
        // eslint-disable-next-line no-await-in-loop
        const { data: job } = await api.get(`/ai/jobs/${jobId}`, { timeout: 15000 });
        if (job.status === "done") return job.result;
        if (job.status === "error") throw new Error(job.error || "Errore generazione");
      }
      throw new Error("Timeout generazione AI");
    } finally {
      if (tickRef.current) clearInterval(tickRef.current);
      tickRef.current = null;
      setLoading(false);
    }
  }, []);

  const cancel = useCallback(() => {
    cancelRef.current = true;
  }, []);

  return { run, loading, elapsedSeconds, cancel };
}
